// The short chime of ticking off a to-do (docs/59: 1b plus the sound of 1c; the chime since 2026-10-20), as the apps
// play it (CompletionSound.swift): the same file, /sounds/chime.wav, made by scripts/completion-sound.py ("Sound
// Effect by José Mejía from Pixabay", credited in About). Once per tick that completes something, also for several at once; never when to-dos open again, and never
// for what arrives from another device, since only a tick calls it. On unless switched off in Settings, in this
// browser (prefs.completionSound).
//
// WebAudio, started only by a tick (a user gesture), so the browser lets it play. The file is fetched and decoded the
// first time; until then the first tick waits the few milliseconds that takes.

// A habit's day has its own sound (docs/63 section 9): /sounds/habit-chime.wav, "Sound Effect by SoundShelfStudio from
// Pixabay", under the same switch; ticking a habit and a to-do together plays the habit's.

import { prefs } from "./prefs";

const URLS = { todo: "/sounds/chime.wav", habit: "/sounds/habit-chime.wav" } as const;

type Context = AudioContext;
type Kind = keyof typeof URLS;
let context: Context | undefined;
const buffers: Partial<Record<Kind, Promise<AudioBuffer | undefined>>> = {};

/** The rule for one tick (CompletionSound.plays): once when it completes something and the setting is on. */
export function playsCompletionSound(completing: number, isOn: boolean): boolean {
  return isOn && completing > 0;
}

function load(ctx: Context, kind: Kind): Promise<AudioBuffer | undefined> {
  return (buffers[kind] ??= fetch(URLS[kind])
    .then((response) => (response.ok ? response.arrayBuffer() : Promise.reject(new Error(String(response.status)))))
    .then((bytes) => ctx.decodeAudioData(bytes))
    .catch(() => {
      // Tried again at the next tick.
      delete buffers[kind];
      return undefined;
    }));
}

/**
 * A tick that completes `completing` to-dos (0 when it opened them again): plays the sound once if it should; with
 * `habit` (a habit's day among them) the habits' sound.
 */
export function completionSound(completing: number, habit = false): void {
  if (!playsCompletionSound(completing, prefs().completionSound)) return;
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    context ??= new Ctx();
    const ctx = context;
    if (ctx.state === "suspended") void ctx.resume();
    void load(ctx, habit ? "habit" : "todo").then((sound) => {
      if (!sound) return;
      const source = ctx.createBufferSource();
      source.buffer = sound;
      source.connect(ctx.destination);
      source.start();
    });
  } catch {
    // No sound in this browser: the tick goes on without it.
  }
}
